// react
import * as React from 'react';
import { type ReactNode } from 'react';

// components
import { ADMIN_VIEW_MODE_STORAGE_KEY } from '@/app/constants';
import {
  AdminViewModeContext,
  type AdminViewModeContextValue,
} from '@/contexts/AdminViewModeContext';
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { useLocalStorage } from '@/hooks/useLocalStorage';

/**
 * Owns whether admin view mode is active, persists the raw preference to
 * localStorage, gates it behind `isAdmin`, and exposes it app-wide via
 * context so the appbar toggle, settings menu toggle, and navigation
 * context stay in sync without prop drilling.
 */
export default function AdminViewModeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { isAdmin, isLoading } = useSessionAuth();

  const [storedAdminViewMode, setStoredAdminViewMode] = useLocalStorage(
    ADMIN_VIEW_MODE_STORAGE_KEY,
    false,
  );

  const adminViewMode = isAdmin && storedAdminViewMode;

  React.useEffect(() => {
    // Wait for the session to finish loading before clearing the stored
    // preference - while `isLoading` is true, `isAdmin` is just the
    // not-yet-resolved default (false), not a confirmed non-admin result.
    if (!isLoading && !isAdmin && storedAdminViewMode) {
      setStoredAdminViewMode(false);
    }
  }, [isLoading, isAdmin, storedAdminViewMode, setStoredAdminViewMode]);

  const setAdminViewMode = (nextValue: boolean) => {
    setStoredAdminViewMode(isAdmin ? Boolean(nextValue) : false);
  };

  const value: AdminViewModeContextValue = { adminViewMode, setAdminViewMode };

  return (
    <AdminViewModeContext.Provider value={value}>
      {children}
    </AdminViewModeContext.Provider>
  );
}
